import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { IconButton, Popover } from "@mui/material";
import { isAxiosError } from "axios";
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isBefore,
  isSameDay,
  isSunday,
  isToday,
  startOfMonth,
  startOfToday,
  startOfWeek,
  subDays,
  subMonths,
  getISODay,
} from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { vi, enUS } from "date-fns/locale"
import { toast } from "react-toastify";
import { EVENT_STATUS } from "../../../assets/data/common";
import CalendarEventLog from "../../../assets/images/icons/CalendarEventLog";
import CalendarEventLogOK from "../../../assets/images/icons/CalendarEventLogOK";
import { imgEventCalendar } from "../../../axios/Eventlog/EventLogApi";
import { useLoading } from "../../../context/LoadingContext";
import { useSettings } from "../../../context/SettingsContext";
import MoreActionClickedTooltip from "../Dashboard/MoreActionClickedTooltip";
import EventTooltip from "./EventTooltip";
import { useAuth } from "../../../context/AuthContext";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ImageIcon from "@mui/icons-material/Image";
import EventImageTooltip from "./EventImageTooltip";
import { DateTime } from "luxon";
import dayjs from 'dayjs';
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ImageIconSvg from "../../../assets/images/icons/ImageIconSvg";

const MAX_DATE_CALENDAR = 35;

const EventImagesCalendar = ({
  selectedShift,
  selectedUser,
  setSelectedDate,
  selectedDate,
  refreshData,
  tabName,
  isShowErrorIMG,
  calculateDisplayedWeeks
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("vi") ? vi : enUS;
  const months = [
    t("common.january"),
    t("common.february"),
    t("common.march"),
    t("common.april"),
    t("common.may"),
    t("common.june"),
    t("common.july"),
    t("common.august"),
    t("common.september"),
    t("common.october"),
    t("common.november"),
    t("common.december"),
  ];
  const weekDays = [
    t("common.monday3"),
    t("common.tuesday3"),
    t("common.wednesday3"),
    t("common.thursday3"),
    t("common.friday3"),
    t("common.saturday3"),
    t("common.sunday3"),
  ];

  const { settings } = useSettings();
  const [show, hide] = useLoading();
  const currentMonthByText = months[selectedDate.getMonth()];
  const currentYear = selectedDate.getFullYear();
  const [events, setEvents] = useState([]);
  const { isAdmin, profile, genaralSettingTimeZone } = useAuth();
  const [legendData, setLegendData] = useState([]);

  const month = selectedDate.getMonth() + 1;
  const year = selectedDate.getFullYear();

  const [currentDate, setCurrentDate] = useState(new Date());

  const handlePrevMonth = () => {
    setCurrentDate(prev =>
      new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    );
    setSelectedDate(subMonths(selectedDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(prev =>
      new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    );
    setSelectedDate(addMonths(selectedDate, 1));
  };


  const { daysInMonth, nextMonthDays, prevMonthDays } = useMemo(() => {
    const date = new Date(year, month - 1, 1);
    const firstDayOfMonth = startOfMonth(date);
    const lastDayOfMonth = endOfMonth(date);

    const daysInMonth =
      events.length > 0
        ? events
        : eachDayOfInterval({
          start: firstDayOfMonth,
          end: lastDayOfMonth,
        }).map((day) => ({ day }));

    // Lấy ngày cuối của tuần trước tháng (để hiển thị ngày đầu tháng đúng vị trí)
    const prevMonthDays =
      getISODay(firstDayOfMonth) === 1
        ? []
        : eachDayOfInterval({
          start: startOfWeek(firstDayOfMonth, { weekStartsOn: 1 }),
          end: subDays(firstDayOfMonth, 1),
        });

    const totalFilledDays = daysInMonth.length + prevMonthDays.length;

    const nextMonthDays = eachDayOfInterval({
      start: addDays(lastDayOfMonth, 1),
      end: addDays(lastDayOfMonth, MAX_DATE_CALENDAR - totalFilledDays),
    });

    return { prevMonthDays, daysInMonth, nextMonthDays };
  }, [month, year, events]);

  const getTooltipTitle = (day) => {
    return format(day, `${settings.dateFormat} (EEEE)`, { locale });
  };

  const getMonthRangeUTC = (date) => {
    const start = new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      1,
      0, 0, 0
    ));

    const end = new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth() + 1,
      0,
      23, 59, 59
    ));

    return {
      from: start.toISOString(),
      to: end.toISOString(),
    };
  };

  const getMonthRangeByTZ = (date, timeZone) => {
    const base = DateTime.fromJSDate(date, {
      zone: timeZone || "UTC",
    });

    return {
      from: base.startOf("month").toUTC().toISO(),
      to: base.endOf("month").toUTC().toISO(),
    };
  };

  useEffect(() => {
    async function fetchData() {
      const browserTZ = dayjs.tz.guess();

      const { from, to } = getMonthRangeByTZ(selectedDate, genaralSettingTimeZone);

      try {
        show();
        const payload = {
          from, // ISO UTC
          to,   // ISO UTC
          langId: localStorage.getItem("i18nextLngId"),
          isError: isShowErrorIMG,
          userId: isAdmin ? 0 : profile.userId,
        };
        const { data } = await imgEventCalendar(payload);
        const events = data.data?.map((v) => ({ ...v, day: new Date(v.day) }));
        setEvents(events);
      } catch (error) {
        console.log(error);
        toast.error(error.response.data.message);
      } finally {
        hide();
      }
    }

    fetchData();
  }, [show, hide, month, year, t, refreshData, isShowErrorIMG]);

  useEffect(() => {
    setLegendData([
      {
        color: <div className="rounded-full w-3 h-3 bg-[#0057FF]" />,
        display: t("common.current-day"),
      },
      {
        color: <div className="rounded-full w-3 h-3 bg-[#0EA5E9]" />,
        display: t("common.selected-day"),
      },
      // { color: <div className='rounded-full w-3 h-3 bg-[#EF4444]' />, display: t('common.error-events') },
      // { color: <div className='rounded-full w-3 h-3 bg-[#16A34A]' />, display: t('common.successful-event') },
    ]);
  }, []);

  const MonthYearPicker = ({ selectedDate, onChange }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [year, setYear] = useState(selectedDate.getFullYear());

    const handleOpen = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const open = Boolean(anchorEl);

    const handleSelect = (monthIndex) => {
      const newDate = new Date(year, monthIndex, 1);
      onChange(newDate);
      handleClose();
    };

    useEffect(() => {
      if (selectedDate) {
        const currentDate = new Date(selectedDate);
        calculateDisplayedWeeks(currentDate, weekDays[0]);
      }
    }, [selectedDate, weekDays]);

    return (
      <>
        <button
          onClick={handleOpen}
          className="flex items-center gap-1 border px-3 py-1 rounded shadow-sm bg-white hover:bg-gray-100 h-10"
        >
          <span className="relative text-[#49494B]" style={{ top: "0.05rem" }}>
            {months[selectedDate.getMonth()]} {selectedDate.getFullYear()}
          </span>
          <ArrowDropDownIcon fontSize="small" className="text-[#49494B]" />
        </button>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <div className="p-4 w-64 sm:w-80">
            <div className="flex justify-between items-center mb-3 text-[#757575]">
              <button onClick={() => setYear(year - 1)}>
                <ArrowBackIosNewIcon
                  fontSize="small"
                  className="text-[#757575]"
                />
              </button>
              <span className="font-medium">{year}</span>
              <button onClick={() => setYear(year + 1)}>
                <ArrowForwardIosIcon
                  fontSize="small"
                  className="text-[#757575]"
                />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2 text-[#757575]">
              {months.map((month, index) => (
                <button
                  key={index}
                  className={`py-1 rounded w-full ${selectedDate.getMonth() === index &&
                    selectedDate.getFullYear() === year
                    ? "bg-blue-600 text-white"
                    : "hover:bg-gray-200"
                    }`}
                  onClick={() => handleSelect(index)}
                >
                  {month}
                </button>
              ))}
            </div>
          </div>
        </Popover>
      </>
    );
  };

  return (
    <div className="month-calendar rounded-[10px] px-3 sm:px-3 md:px-3 py-3">
      <div className="relative h-10 flex items-center">
        <IconButton
          size="small"
          onClick={handlePrevMonth}
          className="!absolute left-0"
        >
          <ChevronLeftIcon />
        </IconButton>

        <div className="w-full text-center px-8 sm:px-10">
          <p className="text-base sm:text-lg md:text-xl text-[#1F2937] font-semibold">
            {currentMonthByText}, {currentYear}
          </p>
        </div>

        <IconButton
          size="small"
          onClick={handleNextMonth}
          className="!absolute right-0"
        >
          <ChevronRightIcon />
        </IconButton>
      </div>

      <div className="mt-4">
        <div className="grid grid-cols-7 text-center">
          {weekDays.map((day) => (
            <div key={day}>
              <p className="uppercase font-semibold text-[10px] sm:text-xs text-[#49494B]">
                {day}
              </p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 text-center border-t border-l border-[#DBE8FF]">
          {prevMonthDays.map((day) => {
            const isSelectedDay = selectedDate && isSameDay(day, selectedDate);
            const isCurrentDay = isToday(day);

            return (
              <div
                className="day prev-month border-r border-b border-[#DBE8FF]"
                key={day.toString()}
                style={
                  isSelectedDay
                    ? { backgroundColor: "#F5F8FF", border: "1px solid #0EA5E9" }
                    : isCurrentDay
                    ? {
                        backgroundColor: "#F5F8FF",
                  
                      }
                    : undefined
                }
                onClick={() => setSelectedDate(day)}
              >
                <div className="text-xs">{format(day, "d")}</div>
              </div>
            );
          })}

          {daysInMonth.map(({ day, sumImage }) => {
            const isSelectedDay = selectedDate && isSameDay(day, selectedDate);
            const isCurrentDay = isToday(day);
            const hasImages = Number(sumImage) > 0;

            return (
              <div
                key={day.toString()}
                className="day border-r border-b border-[#DBE8FF] hover:bg-gray-100 cursor-pointer transition"
                style={
                  isSelectedDay
                    ? { backgroundColor: "#F5F8FF", border: "1px solid #0EA5E9" }
                    : isCurrentDay
                    ? {
                        backgroundColor: "#F5F8FF",
              
                      }
                    : undefined
                }
                onClick={() => setSelectedDate(day)}
              >
                <div className="text-xs font-medium">{format(day, "d")}</div>

                <MoreActionClickedTooltip title={getTooltipTitle(day)}>
                  {hasImages ? (
                    <EventImageTooltip
                      value={`${sumImage} ${t("common.content-182")}`}
                    >
                      <div className="border border-[#E5E7EB] bg-[#FFFFFF] rounded-md min-h-[22px] h-[22px] w-full flex items-center justify-center mt-1 px-1 gap-1">
                        <ImageIconSvg />
                        <p className="text-[10px] text-[#D00D00] font-semibold leading-none">
                          {sumImage}
                        </p>
                      </div>
                    </EventImageTooltip>
                  ) : (
                    <EventImageTooltip value={t("common.content-183")}>
                      <div className="border border-[#E5E7EB] rounded-md h-[22px] w-full flex items-center justify-center mt-1">
                        <p className="text-[10px] text-[#E5E7EB] leading-none">
                          {t("common.content-122")}
                        </p>
                      </div>
                    </EventImageTooltip>
                  )}
                </MoreActionClickedTooltip>
              </div>
            );
          })}

          {nextMonthDays.map((day) => {
            const isSelectedDay = selectedDate && isSameDay(day, selectedDate);
            const isCurrentDay = isToday(day);

            return (
              <div
                key={day.toString()}
                className="day next-month border-r border-b border-[#DBE8FF]"
                style={
                  isSelectedDay
                    ? { backgroundColor: "#F5F8FF", border: "1px solid #0EA5E9" }
                    : isCurrentDay
                    ? {
                        backgroundColor: "#F5F8FF",
              
                      }
                    : undefined
                }
                onClick={() => setSelectedDate(day)}
              >
                <div className="text-xs">{format(day, "d")}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-center pt-3 flex-wrap gap-[8px]">
        {legendData.map((value, index) => (
          <div className="flex gap-1 items-center" key={index}>
            {value.color}
            <p className="text-xs">{value.display}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EventImagesCalendar;
